/* eslint-disable @next/next/no-img-element */
'use client'

import * as React from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { usePhotoStack } from '../../../_components/photo-viewer/photo-stack-context'
import { ProjectVideo } from '@web/app/[locale]/(web)/masterfeed/_components/project-video'

interface AssetGalleryProps {
  assetUrls: {
    id: string
    url: string
    optimizedUrl?: string | null
    thumbnailUrls?: { url: string; width: number }[] | null
    type?: string | null
    state?: string | null
  }[]
  commentId?: string
  prevQuery?: string
  postSlug?: string
}

interface TileAsset {
  id: string
  src: string
  thumbnails?: { url: string; width: number }[]
  index: number
  type: string
  url: string
  state?: string
  optimizedUrl?: string | null
}

const TILE_CLASS = 'relative w-full overflow-hidden rounded-2 block [aspect-ratio:16/9] min-h-[1px]'

interface Tile16x9Props {
  asset: TileAsset
  overlayCount?: number
  getAssetHref: (id: string) => string
  warmRoute: (href: string) => void
  openAsset: (href: string) => (e: React.MouseEvent) => void
}

function normalizeThumbs(thumbs?: { url: string; width: number }[] | null) {
  // oxlint-disable-next-line unicorn/no-useless-spread
  return [...(thumbs ?? []).filter(t => t?.url && t?.width)].toSorted((a, b) => a.width - b.width)
}

function buildSrcSet(thumbs: { url: string; width: number }[]) {
  return [...thumbs]
    .toSorted((a, b) => a.width - b.width)
    .map(t => `${t.url} ${t.width}w`)
    .join(', ')
}

// pick a thumb closest to a target width (so blur uses a tiny image, main uses a bigger one)
function pickThumb(thumbs: { url: string; width: number }[], target: number) {
  if (!thumbs.length) {
    return null
  }
  for (const t of thumbs) {
    if (t.width >= target) {
      return t
    }
  }
  return thumbs.at(-1)
}

const SIZES_SINGLE = '(min-width: 1024px) 756px, (min-width: 768px) 927px, min(703px, 100vw)'
const SIZES_TILE = '(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw'

const Tile16x9 = React.memo(function Tile16x9({
  asset,
  overlayCount,
  getAssetHref,
  warmRoute,
  openAsset,
}: Tile16x9Props) {
  const href = getAssetHref(asset.id)
  const onHover = () => warmRoute(href)
  const handleClick = openAsset(href)

  const thumbs = asset.thumbnails ?? []
  const hasThumbs = thumbs.length > 0

  // use the smallest thumb for blurred background, but a larger one for the actual <img>
  const bgSrc = hasThumbs ? thumbs[0].url : asset.optimizedUrl || asset.src
  const displaySrc =
    (hasThumbs ? pickThumb(thumbs, 480)?.url : null) || asset.optimizedUrl || asset.src

  const srcSet = hasThumbs ? buildSrcSet(thumbs) : undefined
  const isVideo = asset.type === 'video'

  if (isVideo) {
    return <ProjectVideo src={asset.url} onClick={handleClick} state={asset.state} />
  }

  return (
    <a
      href={href}
      onMouseEnter={onHover}
      onPointerDown={onHover}
      onClick={handleClick}
      className="group block rounded-2"
      aria-label="Open asset in viewer"
      data-testid="discussion-image-gallery-tile-link"
    >
      <div className={TILE_CLASS}>
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${bgSrc})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(18px)',
            transform: 'scale(1.05)',
          }}
        />

        <img
          src={displaySrc}
          srcSet={srcSet}
          sizes={SIZES_TILE}
          loading="lazy"
          decoding="async"
          fetchPriority="high"
          alt={
            asset.type === 'image' ? `Project image asset ${asset.id}` : `Project asset ${asset.id}`
          }
          className="absolute inset-0 z-[1] w-full h-full block rounded-2 object-contain transition-opacity duration-300"
        />

        {overlayCount && overlayCount > 0 ? (
          <div className="rounded-2 bg-cf-black text-cf-white text-14 tablet:text-lg absolute inset-0 z-10 grid place-items-center bg-opacity-50 font-semibold">
            +{overlayCount} More
          </div>
        ) : null}
      </div>
    </a>
  )
})

interface SingleAssetProps {
  asset: TileAsset
  getAssetHref: (id: string) => string
  warmRoute: (href: string) => void
  openAsset: (href: string) => (e: React.MouseEvent) => void
}

const SingleAsset = React.memo(function SingleAsset({
  asset,
  getAssetHref,
  warmRoute,
  openAsset,
}: SingleAssetProps) {
  const href = getAssetHref(asset.id)
  const onHover = () => warmRoute(href)
  const handleClick = openAsset(href)

  const thumbs = asset.thumbnails ?? []
  const hasThumbs = thumbs.length > 0

  // use the smallest thumb for blur, but a larger one for the main <img>
  const bgSrc = hasThumbs ? thumbs[0].url : asset.optimizedUrl || asset.src
  const displaySrc =
    (hasThumbs ? pickThumb(thumbs, 700)?.url : null) || asset.optimizedUrl || asset.src

  const srcSet = hasThumbs ? buildSrcSet(thumbs) : undefined

  const isVideo = asset.type === 'video'
  if (isVideo) {
    return <ProjectVideo src={asset.url} onClick={handleClick} state={asset.state} />
  }

  return (
    <a
      href={href}
      onMouseEnter={onHover}
      onPointerDown={onHover}
      onClick={handleClick}
      className="block w-full h-[420px] sm:h-auto max-h-[420px] md:max-h-[550px] lg:max-h-[550px]"
      data-testid="discussion-image-gallery-single-asset-link"
      aria-label="Open asset in viewer"
    >
      <div className="relative w-full overflow-hidden rounded-[9px] flex justify-center aspect-[16/9] h-full">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${bgSrc})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(18px)',
            transform: 'scale(1.05)',
          }}
        />

        <img
          src={displaySrc}
          srcSet={srcSet}
          sizes={SIZES_SINGLE}
          loading="lazy"
          decoding="async"
          fetchPriority="high"
          alt={
            asset.type === 'image' ? `Project image asset ${asset.id}` : `Project asset ${asset.id}`
          }
          className="absolute inset-0 z-[1] w-full h-full block rounded-2 object-contain transition-opacity duration-300"
        />
      </div>
    </a>
  )
})

export function AssetGallery({ assetUrls, commentId, prevQuery, postSlug }: AssetGalleryProps) {
  const pathname = usePathname()
  const router = useRouter()
  const searchParams = useSearchParams()
  const stack = usePhotoStack()

  const isInViewer = React.useMemo(() => /\/photo\/?$/.test(pathname || ''), [pathname])
  const currentAssetId = searchParams.get('assetid')
  const currentCid = searchParams.get('cid')

  const srcByIdRef = React.useRef<Map<string, string>>(new Map())
  const orderByIdRef = React.useRef<Map<string, number>>(new Map())
  const nextOrderRef = React.useRef<number>(0)

  const allAssets = React.useMemo<TileAsset[]>(() => {
    const nextSrc = new Map(srcByIdRef.current)
    const nextOrder = new Map(orderByIdRef.current)
    let nextOrderCounter = nextOrderRef.current

    const mappedRaw = (assetUrls ?? []).map(asset => {
      const thumbs = normalizeThumbs(asset.thumbnailUrls)
      const hasThumbs = thumbs.length > 0
      const fallbackSrc = hasThumbs ? thumbs[0].url : asset.optimizedUrl || asset.url
      const desiredSrc = nextSrc.get(asset.id) ?? fallbackSrc

      const frozenSrc = nextSrc.get(asset.id) ?? desiredSrc
      if (!nextSrc.has(asset.id)) {
        nextSrc.set(asset.id, desiredSrc)
      }

      if (!nextOrder.has(asset.id)) {
        nextOrder.set(asset.id, nextOrderCounter++)
      }
      const frozenOrder = nextOrder.get(asset.id) as number

      return {
        id: asset.id,
        src: frozenSrc,
        thumbnails: hasThumbs ? thumbs : undefined,
        url: asset.url,
        type: asset.type || 'image',
        state: asset.state || 'ready',
        index: frozenOrder,
      } satisfies TileAsset
    })

    srcByIdRef.current = nextSrc
    orderByIdRef.current = nextOrder
    nextOrderRef.current = nextOrderCounter

    return [...mappedRaw].toSorted((a, b) => a.index - b.index)
  }, [assetUrls])

  const count = allAssets.length
  const firstFive = allAssets.slice(0, 5)

  const base = React.useMemo(() => {
    const root = (pathname || '/').replace(/\/photo\/?$/, '').replace(/\/tutorial\/?$/, '')
    return root.endsWith('/') ? root : `${root}/`
  }, [pathname])

  const getAssetHref = React.useCallback(
    (assetId: string) => {
      const params = new URLSearchParams()
      params.set('assetid', assetId)
      // oxlint-disable-next-line eqeqeq
      if (commentId != null) {
        params.set('cid', String(commentId))
      }
      if (prevQuery) {
        params.set('prev', prevQuery)
      }
      // oxlint-disable-next-line eqeqeq
      if (postSlug != null) {
        params.set('postslug', postSlug)
      }
      return `${base}photo?${params.toString()}`
    },
    [base, commentId, prevQuery, postSlug],
  )

  const warmRoute = React.useMemo(() => {
    const warmed = new Set<string>()
    return (href: string) => {
      if (warmed.has(href)) {
        return
      }
      warmed.add(href)
      try {
        router.prefetch?.(href)
      } catch {}
    }
  }, [router])

  const openAsset = React.useCallback(
    (href: string) => (e: React.MouseEvent) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) {
        return
      }

      e.preventDefault()
      e.stopPropagation()

      if (isInViewer && currentAssetId) {
        stack.push({
          assetid: currentAssetId,
          cid: currentCid ?? undefined,
          postslug: postSlug ?? undefined,
        })
      }

      warmRoute(href)
      router.push(href, { scroll: false })
    },
    [router, warmRoute, isInViewer, currentAssetId, currentCid, postSlug, stack],
  )

  if (count <= 0) {
    return null
  }

  return (
    <div className="mt-5 h-full">
      {count === 1 ? (
        <SingleAsset
          key={allAssets[0].id}
          asset={allAssets[0]}
          getAssetHref={getAssetHref}
          warmRoute={warmRoute}
          openAsset={openAsset}
        />
      ) : // oxlint-disable-next-line no-nested-ternary
      count === 2 ? (
        <div className="grid grid-cols-2 gap-2">
          <Tile16x9
            key={allAssets[0].id}
            asset={allAssets[0]}
            getAssetHref={getAssetHref}
            warmRoute={warmRoute}
            openAsset={openAsset}
          />
          <Tile16x9
            key={allAssets[1].id}
            asset={allAssets[1]}
            getAssetHref={getAssetHref}
            warmRoute={warmRoute}
            openAsset={openAsset}
          />
        </div>
      ) : // oxlint-disable-next-line no-nested-ternary
      count === 3 ? (
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2">
            <Tile16x9
              key={allAssets[0].id}
              asset={allAssets[0]}
              getAssetHref={getAssetHref}
              warmRoute={warmRoute}
              openAsset={openAsset}
            />
          </div>
          <Tile16x9
            key={allAssets[1].id}
            asset={allAssets[1]}
            getAssetHref={getAssetHref}
            warmRoute={warmRoute}
            openAsset={openAsset}
          />
          <Tile16x9
            key={allAssets[2].id}
            asset={allAssets[2]}
            getAssetHref={getAssetHref}
            warmRoute={warmRoute}
            openAsset={openAsset}
          />
        </div>
      ) : // oxlint-disable-next-line unicorn/no-nested-ternary
      count === 4 ? (
        <div className="grid grid-cols-2 gap-2">
          {allAssets.slice(0, 4).map(asset => (
            <Tile16x9
              key={asset.id}
              asset={asset}
              getAssetHref={getAssetHref}
              warmRoute={warmRoute}
              openAsset={openAsset}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Tile16x9
              key={firstFive[0].id}
              asset={firstFive[0]}
              getAssetHref={getAssetHref}
              warmRoute={warmRoute}
              openAsset={openAsset}
            />
            <Tile16x9
              key={firstFive[1].id}
              asset={firstFive[1]}
              getAssetHref={getAssetHref}
              warmRoute={warmRoute}
              openAsset={openAsset}
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Tile16x9
              key={firstFive[2].id}
              asset={firstFive[2]}
              getAssetHref={getAssetHref}
              warmRoute={warmRoute}
              openAsset={openAsset}
            />
            <Tile16x9
              key={firstFive[3].id}
              asset={firstFive[3]}
              getAssetHref={getAssetHref}
              warmRoute={warmRoute}
              openAsset={openAsset}
            />
            <Tile16x9
              key={firstFive[4].id}
              asset={firstFive[4]}
              overlayCount={allAssets.length > 5 ? allAssets.length - 5 : 0}
              getAssetHref={getAssetHref}
              warmRoute={warmRoute}
              openAsset={openAsset}
            />
          </div>
        </div>
      )}
    </div>
  )
}
